import loadJob from './queries/load-job';
import createRemoteJob from './queries/create-job';
import getJobParam from './helpers/get-job-param';
import { createNewJob, updateJob } from './store';
import { JobStatus, JOB_POLL_DELAY, JOB_POLL_RETRY } from './constants';

function isJobPending(job) {
  return (
    job.status === JobStatus.Created ||
    job.status === JobStatus.Pending ||
    job.status === JobStatus.Processing ||
    job.status === JobStatus.Queued
  );
}

function isJobCompleted(job) {
  return job.status === JobStatus.Completed || job.status === JobStatus.CompletedWithoutUpdate;
}

function didJobFail(job) {
  return job.status === JobStatus.Failed;
}

function getJobError(job) {
  return job.data.filter(v => v.key === 'errorMessage').map(v => v.value)[0] || 'Unknown error';
}

function loadRemoteJob(job) {
  const url = getJobParam(job, 'mediaUrl');
  return loadJob({ 'params.key': 'mediaUrl', 'params.value': url, limit: 1 }).then(jobs => {
    if (Array.isArray(jobs) && jobs.length > 0) {
      return jobs[0];
    }
    return null;
  });
}

function loadRemoteJobWithRetries(job, retry = 0) {
  return loadRemoteJob(job).catch(err => {
    if (retry > 0) {
      return loadRemoteJobWithRetries(job, retry - 1);
    }

    return Promise.reject(err);
  });
}

function schedulePoll(job) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      loadRemoteJobWithRetries(job, JOB_POLL_RETRY)
        .then(remote => {
          // eslint-disable-next-line no-use-before-define
          monitorJob(updateJob(job, remote))
            .then(resolve)
            .catch(reject);
        })
        .catch(reject);
    }, JOB_POLL_DELAY);
  });
}

function monitorJob(job) {
  /* eslint-disable no-use-before-define */
  if (isJobPending(job)) {
    return schedulePoll(job);
  }
  if (isJobCompleted(job)) {
    return Promise.resolve(job);
  }
  if (didJobFail(job)) {
    return Promise.reject(new Error(getJobError(job)));
  }
  return Promise.reject(new Error(`Unhandled job status ${job.status}`));
  /* eslint-enable no-use-before-define */
}

function createJob(data, force = false) {
  const localJob = createNewJob(data);

  return Promise.resolve(force ? undefined : loadRemoteJob(localJob))
    .then(remoteJob => remoteJob || createRemoteJob(localJob.toJS()))
    .then(remoteJob => updateJob(localJob, remoteJob))
    .catch(() => updateJob(localJob, { status: JobStatus.Failed }));
}

function start(data, force = false) {
  return createJob(data, force).then(monitorJob);
}

export default {
  start,
};
